import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL, formatDate } from '../lib/utils';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Shield, Lock, Unlock, RefreshCw, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';

const SecurityManagement = () => {
  usePageTitle('Security Management');
  const [lockouts, setLockouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlockEmail, setUnlockEmail] = useState(null);
  const [unlocking, setUnlocking] = useState(false);

  const fetchLockouts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/auth/lockouts`);
      setLockouts(response.data);
    } catch (error) {
      toast.error('Failed to fetch lockout data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLockouts();
  }, []);

  const handleUnlock = async () => {
    if (!unlockEmail) return;
    
    setUnlocking(true);
    try {
      await axios.delete(`${API_URL}/auth/lockouts/${encodeURIComponent(unlockEmail)}`);
      toast.success(`Account ${unlockEmail} has been unlocked`);
      setUnlockEmail(null);
      fetchLockouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock account');
    } finally {
      setUnlocking(false);
    }
  };

  const activeLockouts = lockouts.filter(l => l.is_currently_locked);
  const recentAttempts = lockouts.filter(l => !l.is_currently_locked && l.failed_attempts > 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="security-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Security Management
          </h1>
          <p className="text-slate-500 mt-1">
            Monitor and manage account lockouts
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchLockouts}
          disabled={loading}
          data-testid="refresh-lockouts-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${activeLockouts.length > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                {activeLockouts.length > 0 ? (
                  <Lock className="w-6 h-6 text-red-600" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                )}
              </div>
              <div>
                <p className="text-2xl font-bold">{activeLockouts.length}</p>
                <p className="text-sm text-slate-500">Currently Locked</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{recentAttempts.length}</p>
                <p className="text-sm text-slate-500">With Failed Attempts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{lockouts.length}</p>
                <p className="text-sm text-slate-500">Total Records</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Lockouts */}
      {activeLockouts.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Lock className="w-5 h-5" />
              Active Lockouts
            </CardTitle>
            <CardDescription>
              These accounts are currently locked and cannot log in
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Lockout Count</TableHead>
                  <TableHead>Time Remaining</TableHead>
                  <TableHead>Last IP</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeLockouts.map((lockout) => (
                  <TableRow key={lockout.email}>
                    <TableCell className="font-medium">{lockout.email}</TableCell>
                    <TableCell>
                      <Badge variant={lockout.lockout_count >= 3 ? 'destructive' : 'secondary'}>
                        {lockout.lockout_count} lockout(s)
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-red-600 font-medium">
                        {lockout.remaining_minutes} min remaining
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {lockout.last_failed_ip}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setUnlockEmail(lockout.email)}
                        data-testid={`unlock-btn-${lockout.email}`}
                      >
                        <Unlock className="w-4 h-4 mr-2" />
                        Unlock
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* All Lockout Records */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            All Lockout Records
          </CardTitle>
          <CardDescription>
            History of failed login attempts and lockouts
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : lockouts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No lockout records found</p>
              <p className="text-sm">All accounts are in good standing</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Failed Attempts</TableHead>
                  <TableHead>Lockout Count</TableHead>
                  <TableHead>Last Attempt</TableHead>
                  <TableHead>Last IP</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lockouts.map((lockout) => (
                  <TableRow key={lockout.email}>
                    <TableCell className="font-medium">{lockout.email}</TableCell>
                    <TableCell>
                      {lockout.is_currently_locked ? (
                        <Badge variant="destructive">
                          <Lock className="w-3 h-3 mr-1" />
                          Locked ({lockout.remaining_minutes}m)
                        </Badge>
                      ) : lockout.failed_attempts > 0 ? (
                        <Badge variant="secondary">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {lockout.failed_attempts} attempt(s)
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-green-600 border-green-300">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Clear
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{lockout.failed_attempts}</TableCell>
                    <TableCell>
                      {lockout.lockout_count > 0 && (
                        <Badge variant={lockout.lockout_count >= 3 ? 'destructive' : 'secondary'}>
                          {lockout.lockout_count}x
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {lockout.last_failed_attempt ? formatDate(lockout.last_failed_attempt) : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {lockout.last_failed_ip || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setUnlockEmail(lockout.email)}
                        data-testid={`clear-btn-${lockout.email}`}
                      >
                        <Unlock className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Unlock Confirmation Dialog */}
      <AlertDialog open={!!unlockEmail} onOpenChange={() => setUnlockEmail(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlock Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unlock <strong>{unlockEmail}</strong>?
              This will clear all failed login attempts and allow the user to log in immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unlocking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnlock}
              disabled={unlocking}
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8]"
            >
              {unlocking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Unlocking...
                </>
              ) : (
                <>
                  <Unlock className="w-4 h-4 mr-2" />
                  Unlock Account
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SecurityManagement;
